import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useMutation, useQuery } from '@apollo/client';

import { useDebounce, useI18n, useLogger, useSelector } from '~Hooks';
import useConfirm from '~Hooks/useConfirm';
import { useStorage } from '~Components/Storage';
import { useTableUniverse } from '~Components/TableUniverse';
import {
  HOTEL_CHARGE_CHEQUE_TO_ROOM,
  HOTEL_CORRECT_ROOM_CHARGE,
  HOTEL_RESOLVE_ROOM_CHARGE,
  HOTEL_SEARCH_GUESTS,
  HOTEL_SEARCH_PM_ACCOUNTS,
} from '~Queries/hotel';
import {
  HotelChargeChequeToRoom,
  HotelChargeChequeToRoomVariables,
  HotelCorrectRoomCharge,
  HotelCorrectRoomChargeVariables,
  HotelResolveRoomCharge,
  HotelResolveRoomChargeVariables,
  HotelSearchGuests,
  HotelSearchGuestsVariables,
  HotelSearchPmAccounts,
  HotelSearchPmAccountsVariables,
} from '~Queries/__queries__';
import { MethodProps } from '~Components/Payments/types';
import {
  HotelChargeTargetKind,
  HotelRoomChargeOutcome,
} from '../../../../__queries__/globalTypes';
import { chequeItems, hotelErrorOf } from './hotel-room';
import {
  HotelRoomStep,
  HotelSearchFailure,
  HotelSearchHit,
  HotelSearchTab,
  HotelTarget,
} from './types';

export * from './types';

const CONFLICT_RETRIES = 5;
const CONFLICT_DELAY_MS = 3000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type Params = Pick<
  MethodProps,
  | 'payment'
  | 'order'
  | 'amount'
  | 'orderAmount'
  | 'discount'
  | 'venueId'
  | 'onComplete'
  | 'onFail'
>;

/**
 * All business logic for charging a bill to a hotel room: guest / account search,
 * the charge with conflict retries, undo, and the pending-charge bookkeeping in storage.
 * The HotelRoom payment method only renders what this returns.
 */
export const useHotelRoomPayment = ({
  payment,
  order,
  amount,
  orderAmount,
  discount,
  venueId,
  onComplete,
  onFail,
}: Params) => {
  const { i18n } = useI18n();
  const logger = useLogger('payment:hotel-room');
  const splitOrder = useSelector((state) => state.POSSplitOrder);
  const staffId = useSelector((state) => state.staff?.id);
  const universe = useTableUniverse();
  const [storage, updateStorage] = useStorage();
  const storageRef = useRef(storage);
  const { confirm } = useConfirm();

  useEffect(() => {
    storageRef.current = storage;
  }, [storage]);

  const [tab, setTab] = useState<HotelSearchTab>('GUEST');
  const [search, setSearch] = useState('');
  const query = useDebounce(search.trim(), 400) as string;
  const [step, setStep] = useState<HotelRoomStep>({ kind: 'SEARCH' });

  const [charge] = useMutation<HotelChargeChequeToRoom, HotelChargeChequeToRoomVariables>(
    HOTEL_CHARGE_CHEQUE_TO_ROOM,
    { fetchPolicy: 'no-cache' },
  );
  const [correct] = useMutation<HotelCorrectRoomCharge, HotelCorrectRoomChargeVariables>(
    HOTEL_CORRECT_ROOM_CHARGE,
    { fetchPolicy: 'no-cache' },
  );

  const [resolve] = useMutation<HotelResolveRoomCharge, HotelResolveRoomChargeVariables>(
    HOTEL_RESOLVE_ROOM_CHARGE,
    { fetchPolicy: 'no-cache' },
  );

  const searching = step.kind === 'SEARCH' && query.length >= 2;

  const guests = useQuery<HotelSearchGuests, HotelSearchGuestsVariables>(HOTEL_SEARCH_GUESTS, {
    skip: !searching || tab !== 'GUEST',
    fetchPolicy: 'network-only',
    variables: /^\d+$/.test(query) ? { venueId, roomNumber: query } : { venueId, lastName: query },
  });

  const accounts = useQuery<HotelSearchPmAccounts, HotelSearchPmAccountsVariables>(
    HOTEL_SEARCH_PM_ACCOUNTS,
    {
      skip: !searching || tab !== 'ACCOUNT',
      fetchPolicy: 'network-only',
      variables: { venueId, query },
    },
  );

  /** False when there is nothing to pay, so the method renders nothing. */
  const ready = payment.status === 'PENDING' && Boolean(order) && amount !== undefined;
  const wholeBill = !splitOrder && amount === orderAmount - discount;
  const chequeId = order ? order.chequeId ?? order.id : '';

  /* A bill already sent but not yet an order keeps the tip it was sent with */
  const [pendingCharge] = useState(() =>
    (storage.hotelRoomCharges ?? []).find((entry) => entry.chequeId === chequeId),
  );
  const [tip, setTip] = useState(pendingCharge?.tip ?? 0);
  /** Ask for a tip first, like the other methods, unless the bill was already sent */
  const askForTip = !payment.disableTip && storage.enableTipping && !pendingCharge;

  useEffect(() => {
    if (!wholeBill) {
      logger.warn('Hotel room refused for a partial amount', { amount, orderAmount, discount });
    }
  }, [wholeBill]);

  /** For the hotel event log: a table cheque carries its table, a QSR order its easyId */
  const chequeLabel = () =>
    order?.chequeId
      ? {
          tableId:
            universe.cheques.find((cheque) => cheque.id === order.chequeId)?.tableId || undefined,
        }
      : { orderNumber: order?.orderNumber || undefined };

  const generateEventMeta = () => ({
    staffId: staffId ?? undefined,
    deviceId: storage.taxComplianceDeviceId || undefined,
  });

  const pendingCharges = () => storageRef.current.hotelRoomCharges ?? [];

  function forgetCharge(chargeId: string) {
    updateStorage({
      hotelRoomCharges: pendingCharges().filter((entry) => entry.chargeId !== chargeId),
    });
  }

  /** The charge this cheque replaces, when it was made by reopening a room-paid order */
  const reopenedFrom = () =>
    (storageRef.current.hotelReopenedCheques ?? []).find((entry) => entry.chequeId === chequeId)
      ?.reopenedFrom;

  function forgetReopened() {
    updateStorage({
      hotelReopenedCheques: (storageRef.current.hotelReopenedCheques ?? []).filter(
        (entry) => entry.chequeId !== chequeId,
      ),
    });
  }

  /**
   * Fails rather than calls onCancel: onCancel only drops the payment, leaving the
   * POS order behind, so the scene never resets and every later payWith is a no-op.
   */
  function cancel() {
    logger.info('Hotel room payment cancelled', { chequeId });
    onFail(i18n('Payment.Canceled'));
  }

  async function sendCharge(target: HotelTarget) {
    if (!order || amount === undefined) {
      return;
    }

    const stored = pendingCharges().find((entry) => entry.chequeId === chequeId);
    if (stored && (stored.amount !== amount || (stored.tip ?? 0) !== tip)) {
      logger.warn('Bill changed since it was sent to the hotel', { chequeId, stored, amount, tip });
      setStep({
        kind: 'BLOCKED',
        message: i18n('HotelRoom.ChangedSinceCharge'),
        undoChargeId: stored.chargeId,
      });
      return;
    }

    const chargeId = stored?.chargeId ?? uuid();
    if (!stored) {
      updateStorage({
        hotelRoomCharges: [
          ...pendingCharges(),
          { chequeId, chargeId, amount, tip, createdAt: new Date().toISOString() },
        ],
      });
    }

    setStep({ kind: 'SENDING' });
    const variables: HotelChargeChequeToRoomVariables = {
      input: {
        chargeId,
        cheque: {
          chequeId,
          venueId,
          items: chequeItems(order.items, amount),
          ...chequeLabel(),
        },
        target: target.input,
        tip: tip > 0 ? tip : undefined,
        reopenedFrom: reopenedFrom(),
        ...generateEventMeta(),
      },
    };
    logger.info('Sending bill to hotel room', {
      chargeId,
      chequeId,
      room: target.room,
      amount,
      tip,
      reopenedFrom: variables.input.reopenedFrom,
    });

    let error: string | null = null;
    for (let attempt = 0; attempt <= CONFLICT_RETRIES; attempt += 1) {
      try {
        const { data } = await charge({ variables });
        if (data?.hotelChargeChequeToRoom.success) {
          logger.info('Bill charged to hotel room', { chargeId, chequeId });
          forgetCharge(chargeId);
          forgetReopened();
          onComplete(null, { transactionId: chargeId, ...(tip > 0 ? { tip } : {}) });
          return;
        }
        error = data?.hotelChargeChequeToRoom.error ?? null;
      } catch (err) {
        logger.error('Hotel room charge did not answer', {
          chargeId,
          errorMessage: (err as Error).message,
        });
        error = null;
        break;
      }
      if (hotelErrorOf(error).code !== 'CONFLICT' || attempt === CONFLICT_RETRIES) {
        break;
      }
      await wait(CONFLICT_DELAY_MS);
    }

    const { code, detail } = hotelErrorOf(error);
    logger.warn('Hotel room charge refused', { chargeId, chequeId, code, detail });

    switch (code) {
      case null:
      case 'CONFLICT':
        // No answer, or the first attempt is still with the hotel: the bill may be on the room
        setStep({
          kind: 'RETRY',
          target,
          message: i18n('HotelRoom.NoAnswer'),
          canPayOtherwise: false,
        });
        return;
      case 'PMS_ERROR':
        // Galaxy only sends PMS_ERROR when the hotel booked nothing
        setStep({
          kind: 'RETRY',
          target,
          message: i18n('HotelRoom.Unreachable'),
          canPayOtherwise: true,
        });
        return;
      case 'UNCONFIRMED':
        // Kept in storage: until reception has answered, a retry must use the same chargeId
        setStep({ kind: 'UNCONFIRMED', chargeId });
        return;
      case 'CHEQUE_ALREADY_CHARGED':
        forgetCharge(chargeId);
        setStep({
          kind: 'BLOCKED',
          message: i18n('HotelRoom.AlreadyCharged'),
          undoChargeId: /\(charge ([^)]+)\)/.exec(detail)?.[1],
        });
        return;
      case 'NO_POST':
        forgetCharge(chargeId);
        setStep({ kind: 'BLOCKED', message: i18n('HotelRoom.NoPost') });
        return;
      case 'CHECKED_OUT':
        forgetCharge(chargeId);
        setStep({ kind: 'BLOCKED', message: i18n('HotelRoom.CheckedOut') });
        return;
      case 'MAPPING_MISSING':
        forgetCharge(chargeId);
        if (tip > 0 && /tip/i.test(detail)) {
          // Only the tip can't be booked: drop it so staff can still charge the bill
          setTip(0);
          setStep({ kind: 'CONFIRM', target, notice: i18n('HotelRoom.TipNotSetUp') });
          return;
        }
        setStep({ kind: 'BLOCKED', message: i18n('HotelRoom.NotSetUp') });
        return;
      default:
        forgetCharge(chargeId);
        setStep({ kind: 'BLOCKED', message: i18n('HotelRoom.Error') });
    }
  }

  async function undoCharge(chargeId: string) {
    setStep({ kind: 'SENDING' });
    logger.info('Undoing hotel room charge', { chargeId, chequeId });
    try {
      const { data } = await correct({
        variables: { input: { correctionId: uuid(), chargeId, ...generateEventMeta() } },
      });
      const { code, detail } = hotelErrorOf(data?.hotelCorrectRoomCharge.error);
      if (data?.hotelCorrectRoomCharge.success || code === 'ALREADY_CORRECTED') {
        forgetCharge(chargeId);
        setStep({ kind: 'SEARCH', notice: i18n('HotelRoom.Undone') });
        return;
      }
      if (code === 'UNCONFIRMED') {
        logger.warn('Hotel room charge to undo is unconfirmed', { chargeId, chequeId });
        setStep({ kind: 'UNCONFIRMED', chargeId, thenUndo: true });
        return;
      }
      logger.error('Hotel room charge could not be undone', { chargeId, code, detail });
    } catch (err) {
      logger.error('Hotel room undo did not answer', {
        chargeId,
        errorMessage: (err as Error).message,
      });
    }
    setStep({ kind: 'BLOCKED', message: i18n('HotelRoom.Unreachable'), undoChargeId: chargeId });
  }

  /**
   * Settles a charge the hotel did not confirm, with what reception found on
   * the folio. On the room: paid, unless it came up while undoing. Not there:
   * the bill can be paid again.
   */
  async function resolveCharge(chargeId: string, onFolio: boolean, thenUndo = false) {
    const outcome = onFolio ? HotelRoomChargeOutcome.CHARGED : HotelRoomChargeOutcome.NOT_CHARGED;
    setStep({ kind: 'SENDING' });
    logger.info('Resolving unconfirmed hotel room charge', { chargeId, chequeId, outcome });
    try {
      const { data } = await resolve({
        variables: { input: { chargeId, outcome, ...generateEventMeta() } },
      });
      if (data?.hotelResolveRoomCharge.success) {
        logger.info('Unconfirmed hotel room charge resolved', { chargeId, chequeId, outcome });
        if (onFolio && thenUndo) {
          await undoCharge(chargeId);
          return;
        }
        forgetCharge(chargeId);
        if (onFolio) {
          forgetReopened();
          onComplete(null, { transactionId: chargeId, ...(tip > 0 ? { tip } : {}) });
          return;
        }
        setStep({ kind: 'SEARCH', notice: i18n('HotelRoom.NotCharged') });
        return;
      }
      const { code, detail } = hotelErrorOf(data?.hotelResolveRoomCharge.error);
      logger.error('Unconfirmed hotel room charge could not be resolved', {
        chargeId,
        outcome,
        code,
        detail,
      });
    } catch (err) {
      logger.error('Hotel room resolve did not answer', {
        chargeId,
        errorMessage: (err as Error).message,
      });
    }
    setStep({ kind: 'UNCONFIRMED', chargeId, thenUndo, notice: i18n('HotelRoom.ResolveFailed') });
  }

  /** Gives up on the hotel room; drops the pending charge when one is passed. */
  function otherPayment(chargeId?: string) {
    logger.info('Switching from hotel room to another payment', { chequeId, chargeId });
    if (chargeId) {
      forgetCharge(chargeId);
    }
    onFail(i18n('Payment.Canceled'));
  }

  /** After a failed send, the charge still pending for this cheque is dropped too. */
  function otherPaymentAfterRetry() {
    otherPayment(pendingCharges().find((entry) => entry.chequeId === chequeId)?.chargeId);
  }

  /**
   * Takes the tip as typed in the numpad. An unusually large tip needs a
   * confirmation, as in the other methods: staff have typed the payment
   * amount into the tip prompt. Returns false to keep the numpad open.
   */
  async function enterTip(value: string): Promise<boolean> {
    const typed = parseFloat(value);
    const next = isNaN(typed) ? 0 : Math.round(typed * 100);
    if (amount !== undefined && amount > 0 && next >= amount * 0.5) {
      const confirmed = await confirm(i18n('Payment.ConfirmLargeTip', typed, amount / 100)).catch(
        () => false,
      );
      if (!confirmed) {
        return false;
      }
    }
    logger.info('Hotel room tip set', { chequeId, tip: next });
    setTip(next);
    return true;
  }

  function selectTarget(hit: HotelSearchHit) {
    logger.info('Hotel room target selected', { chequeId, room: hit.room, kind: hit.input.kind });
    setStep({ kind: 'CONFIRM', target: hit });
  }

  function backToSearch() {
    setStep({ kind: 'SEARCH' });
  }

  const searchError = tab === 'GUEST' ? guests.error : accounts.error;
  const searchFailure: HotelSearchFailure = searchError
    ? hotelErrorOf(searchError.message).code === 'MAPPING_MISSING'
      ? 'MAPPING_MISSING'
      : 'UNREACHABLE'
    : null;
  const loading = tab === 'GUEST' ? guests.loading : accounts.loading;
  const results: HotelSearchHit[] =
    tab === 'GUEST'
      ? (guests.data?.hotelSearchGuests ?? []).map((guest) => ({
          key: guest.id,
          noPost: guest.noPost,
          room: guest.roomNumber,
          name: [guest.lastName, guest.firstName].filter(Boolean).join(', '),
          input: {
            kind: HotelChargeTargetKind.RESERVATION,
            reservationId: guest.id,
            guestId: guest.guestId,
            roomNumber: guest.roomNumber,
          },
        }))
      : (accounts.data?.hotelSearchPmAccounts ?? []).map((account) => ({
          key: account.id,
          noPost: account.noPost,
          room: account.number,
          name: account.name,
          input: {
            kind: HotelChargeTargetKind.PM_ACCOUNT,
            accountId: account.id,
            roomNumber: account.number,
          },
        }));

  return {
    ready,
    wholeBill,
    step,
    tip,
    askForTip,
    enterTip,
    tab,
    setTab,
    search,
    setSearch,
    searching,
    loading,
    searchFailure,
    results,
    selectTarget,
    backToSearch,
    sendCharge,
    undoCharge,
    resolveCharge,
    cancel,
    otherPayment,
    otherPaymentAfterRetry,
  };
};
